import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import { Field, Skeleton, Icon } from '../components/ui';
import { api, type Asset, type Job } from '../lib/api';
import { useAction, useLoad } from '../lib/hooks';
import { useAuth } from '../store/auth';
import { today } from '../../../shared/schedule';
import './screens.css';

interface Part {
  desc: string;
  qty: number | string;
  unitPrice: number | string;
}

const CATEGORIES = [
  { value: 'repair', label: 'Repair' },
  { value: 'service', label: 'Service' },
  { value: 'mot_prep', label: 'MOT prep' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'other', label: 'Other' },
];

export default function JobForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const tenant = useAuth((s) => s.tenant);
  const { busy, error, run } = useAction();

  const [assetId, setAssetId] = useState(params.get('assetId') ?? '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('repair');
  const [status, setStatus] = useState<'open' | 'in_progress' | 'complete'>('open');
  const [openedAt, setOpenedAt] = useState(today());
  const [odometer, setOdometer] = useState('');
  const [labourHours, setLabourHours] = useState('0');
  const [labourRate, setLabourRate] = useState('');
  const [parts, setParts] = useState<Part[]>([]);
  const [loaded, setLoaded] = useState(false);

  const { data: fleet } = useLoad<{ assets: Asset[] }>(() => api.get('/assets'));
  const { data: jobData, loading } = useLoad<{ jobs: Job[] } | null>(
    () => (editing ? api.get('/jobs') : Promise.resolve(null)),
    [id],
  );

  const job = useMemo(() => jobData?.jobs.find((j) => j.id === id) ?? null, [jobData, id]);

  useEffect(() => {
    if (labourRate === '' && tenant?.labourRate) setLabourRate(String(tenant.labourRate));
  }, [tenant, labourRate]);

  useEffect(() => {
    if (!job || loaded) return;
    setAssetId(job.asset_id);
    setTitle(job.title);
    setDescription(job.description ?? '');
    setCategory(job.category ?? 'repair');
    setStatus(job.status === 'invoiced' ? 'complete' : job.status);
    setOpenedAt(job.opened_at);
    setOdometer(job.odometer != null ? String(job.odometer) : '');
    setLabourHours(String(job.labour_hours));
    setLabourRate(String(job.labour_rate));
    setParts(job.parts ?? []);
    setLoaded(true);
  }, [job, loaded]);

  const total = useMemo(() => {
    const labour = (Number(labourHours) || 0) * (Number(labourRate) || 0);
    const partsTotal = parts.reduce((sum, p) => sum + (Number(p.qty) || 0) * (Number(p.unitPrice) || 0), 0);
    return labour + partsTotal;
  }, [labourHours, labourRate, parts]);

  const locked = job?.status === 'invoiced';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      const payload = {
        assetId,
        title,
        description,
        category,
        status,
        openedAt,
        odometer: odometer === '' ? null : Number(odometer),
        labourHours: Number(labourHours) || 0,
        labourRate: Number(labourRate) || 0,
        parts: parts
          .filter((p) => p.desc.trim())
          .map((p) => ({ desc: p.desc, qty: Number(p.qty) || 0, unitPrice: Number(p.unitPrice) || 0 })),
      };
      if (editing) await api.patch(`/jobs/${id}`, payload);
      else await api.post('/jobs', payload);
      navigate('/jobs');
    });
  };

  if (editing && loading && !job) {
    return (
      <Screen title="Job" back="/jobs">
        <Skeleton variant="detail" rows={4} />
      </Screen>
    );
  }

  return (
    <Screen title={editing ? (job?.ref ?? 'Job') : 'New job'} back="/jobs" hideTabs>
      <form className="stack-5" onSubmit={submit}>
        {error ? <div className="notice notice-danger">{error}</div> : null}
        {locked ? (
          <div className="notice notice-info">
            <span className="small">This job has been invoiced, so its figures are fixed.</span>
          </div>
        ) : null}

        <Field label="Vehicle">
          <select
            className="select"
            required
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
            disabled={locked}
          >
            <option value="">— Choose a vehicle —</option>
            {(fleet?.assets ?? []).map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.reg_no ?? asset.fleet_no} — {[asset.make, asset.model].filter(Boolean).join(' ')}
              </option>
            ))}
          </select>
        </Field>

        <Field label="What was done">
          <input
            className="input"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Replace N/S front spray flap"
            disabled={locked}
          />
        </Field>

        <Field label="Detail">
          <textarea
            className="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={locked}
          />
        </Field>

        <div className="grid-2 collapse">
          <Field label="Category">
            <select className="select" value={category} onChange={(e) => setCategory(e.target.value)} disabled={locked}>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Date opened">
            <input
              className="input"
              type="date"
              value={openedAt}
              onChange={(e) => setOpenedAt(e.target.value)}
              disabled={locked}
            />
          </Field>
        </div>

        <Field label="Status">
          <div className="segmented">
            {(['open', 'in_progress', 'complete'] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={`segment${status === s ? ' is-on' : ''}`}
                onClick={() => setStatus(s)}
                disabled={locked}
              >
                {s === 'in_progress' ? 'In progress' : s === 'open' ? 'Open' : 'Complete'}
              </button>
            ))}
          </div>
        </Field>

        <fieldset className="stack-4 fieldset">
          <legend className="eyebrow">Labour</legend>
          <div className="grid-2 collapse">
            <Field label="Hours">
              <input
                className="input"
                type="number"
                step="0.25"
                inputMode="decimal"
                value={labourHours}
                onChange={(e) => setLabourHours(e.target.value)}
                disabled={locked}
              />
            </Field>
            <Field label="Rate (£/hr)">
              <input
                className="input"
                type="number"
                step="0.01"
                inputMode="decimal"
                value={labourRate}
                onChange={(e) => setLabourRate(e.target.value)}
                disabled={locked}
              />
            </Field>
          </div>
        </fieldset>

        <fieldset className="stack-2 fieldset">
          <legend className="eyebrow">Parts</legend>
          {parts.map((part, index) => (
            <div key={index} className="partrow">
              <input
                className="input"
                placeholder="Part"
                value={part.desc}
                onChange={(e) =>
                  setParts((p) => p.map((x, i) => (i === index ? { ...x, desc: e.target.value } : x)))
                }
                disabled={locked}
              />
              <input
                className="input part-qty"
                type="number"
                step="0.01"
                inputMode="decimal"
                placeholder="Qty"
                aria-label="Quantity"
                value={part.qty}
                onChange={(e) =>
                  setParts((p) => p.map((x, i) => (i === index ? { ...x, qty: e.target.value } : x)))
                }
                disabled={locked}
              />
              <input
                className="input part-price"
                type="number"
                step="0.01"
                inputMode="decimal"
                placeholder="£"
                aria-label="Unit price"
                value={part.unitPrice}
                onChange={(e) =>
                  setParts((p) => p.map((x, i) => (i === index ? { ...x, unitPrice: e.target.value } : x)))
                }
                disabled={locked}
              />
              <button
                type="button"
                className="btn btn-quiet part-remove"
                onClick={() => setParts((p) => p.filter((_, i) => i !== index))}
                aria-label={`Remove ${part.desc || 'part'}`}
                disabled={locked}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setParts((p) => [...p, { desc: '', qty: 1, unitPrice: '' }])}
            disabled={locked}
          >
            <Icon name="plus" size={15} />
            Add a part
          </button>
        </fieldset>

        <div className="card card-pad row spread">
          <span className="label">Job total</span>
          <span className="money" style={{ fontSize: '1.25rem' }}>
            £{total.toFixed(2)}
          </span>
        </div>

        <div className="actionbar">
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/jobs')}>
            Cancel
          </button>
          <button className="btn btn-primary" type="submit" disabled={busy || locked}>
            {busy ? 'Saving…' : editing ? 'Save job' : 'Open job'}
          </button>
        </div>
      </form>
    </Screen>
  );
}
